import { PRE_ORDER_TABLE } from "./resources/orders/pre-order/db"
import { isDbClosedError } from "./is-db-closed-error"
import { hydratePreOrderRows, type OnDemandDeps } from "./on-demand-hydrate"

/**
 * Single-PO ON-DEMAND fetch by id (STK-1972).
 *
 * When `/recap/:id` targets a pre-order outside the kept window (a stock-page
 * history-popover click, a deep-link, browser-back), it isn't on device. This
 * fetches that one PO + its flows from the sync-shape endpoint
 * (`GET /orders/pre-orders/on-demand/:id`) and upserts them into the Dexie
 * `preOrders` + `preOrderFlows` tables so the focus route's reactive read picks
 * them up. Hydration (shield + writes + `_ephemeral` stamping) is the shared
 * path in `on-demand-hydrate.ts`, same as the window fetch (STK-1323) — here
 * `atomic: true`: one order + its flows is a tiny batch, and the transaction
 * makes the shield check-and-write race-free and prevents a half-written
 * order (header without lines). Rationale in `on-demand-hydrate.ts`.
 *
 * Invariants (identical to `on-demand.ts`):
 *  - Offline / fetch failure → `{ found:false, offlineMiss:true }`, ZERO writes.
 *  - Server returns no PO (absent / not-visible) → `{ found:false,
 *    offlineMiss:false }` — a clean miss the route surfaces as "introuvable".
 *  - Respects the change-queue shield: a row with a pending local edit is never
 *    clobbered (but the PO still reports `found` — it exists).
 */

const ON_DEMAND_BY_ID_URL = "/api/v2/orders/pre-orders/on-demand"

export interface FetchPreOrderByIdResult {
  found: boolean
  offlineMiss: boolean
}

interface OnDemandByIdResponse {
  preOrders: Array<{ id: string }>
  flows: Array<{ id: string }>
}

export async function fetchPreOrderById(
  id: string,
  deps: OnDemandDeps
): Promise<FetchPreOrderByIdResult> {
  if (!deps.isOnline()) return { found: false, offlineMiss: true }

  let response: OnDemandByIdResponse
  try {
    response = await deps.httpClient.get<OnDemandByIdResponse>(
      `${ON_DEMAND_BY_ID_URL}/${id}`
    )
  } catch {
    return { found: false, offlineMiss: true }
  }

  // Server has no such visible PO → a clean miss, no writes.
  if (response.preOrders.length === 0) {
    return { found: false, offlineMiss: false }
  }

  let pos: Array<{ id: string }>
  try {
    pos = (await hydratePreOrderRows(response, deps, { atomic: true })).pos
  } catch (err) {
    if (isDbClosedError(err)) return { found: false, offlineMiss: true }
    throw err
  }

  // `found` must reflect whether the target row is actually on device now, not
  // merely that the server returned it. When the change-queue shield drops the
  // target from the write, report found only if a local row already exists —
  // otherwise the focus route waits forever for a hydration that never lands
  // (row absent AND shielded). The extra read only runs in that rare branch.
  if (pos.some((p) => p.id === id)) return { found: true, offlineMiss: false }
  const existing = await deps.storage.get(PRE_ORDER_TABLE, id)
  return { found: existing != null, offlineMiss: false }
}
